import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Plus, Loader2, Briefcase, Calendar, ChevronRight } from 'lucide-react'
import matchingService from '@/services/matchingService'
import { toast } from '@/stores/toastStore'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import type { AIMatchSummary } from '@/types'

interface JobGroup {
  recruiterJobId: string
  recruiterJobTitle: string
  matches: AIMatchSummary[]
  lastSearchedAt: string
  totalSearches: number
  totalCandidates: number
}

function groupMatchesByJob(matches: AIMatchSummary[]): JobGroup[] {
  const map = new Map<string, JobGroup>()

  for (const match of matches) {
    const jobId = match.recruiterJobId || 'unknown'
    const jobTitle = match.recruiterJobTitle || 'Untitled Job'

    if (!map.has(jobId)) {
      map.set(jobId, {
        recruiterJobId: jobId,
        recruiterJobTitle: jobTitle,
        matches: [],
        lastSearchedAt: match.createdAt,
        totalSearches: 0,
        totalCandidates: 0,
      })
    }

    const group = map.get(jobId)!
    group.matches.push(match)
    group.totalSearches += 1

    if (match.status === 'completed' && match.totalMatches) {
      group.totalCandidates += match.totalMatches
    }

    if (new Date(match.createdAt) > new Date(group.lastSearchedAt)) {
      group.lastSearchedAt = match.createdAt
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.lastSearchedAt).getTime() - new Date(a.lastSearchedAt).getTime()
  )
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function AIMatching() {
  const navigate = useNavigate()
  const [jobGroups, setJobGroups] = useState<JobGroup[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadMatches()
  }, [])

  const loadMatches = async () => {
    setLoading(true)
    try {
      const response = await matchingService.listMatches({ limit: 50 })
      setJobGroups(groupMatchesByJob(response.matches))
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load AI matches')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-blue-600" />
            AI Candidate Matching
          </h1>
          <p className="mt-2 text-gray-600">Select a job to view all matched candidates</p>
        </div>
        <Button
          onClick={() => navigate('/recruiter/ai-matching/create')}
          variant="primary"
          className="flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New AI Match
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {!loading && jobGroups.length === 0 && (
        <Card className="text-center py-12">
          <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No AI Matches Yet</h3>
          <p className="text-gray-600 mb-6">
            Run your first AI match to find the best-matching candidates for your jobs.
          </p>
          <Button
            onClick={() => navigate('/recruiter/ai-matching/create')}
            variant="primary"
            className="inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Start Your First AI Match
          </Button>
        </Card>
      )}

      {!loading && jobGroups.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {jobGroups.map((group) => (
            <button
              key={group.recruiterJobId}
              onClick={() => navigate(`/recruiter/ai-matching/job/${group.recruiterJobId}`)}
              className="text-left w-full"
            >
              <Card className="h-full hover:shadow-md hover:border-blue-200 transition-all cursor-pointer border border-gray-200">
                <div className="p-5">
                  {/* Title row */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <Briefcase className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <h3 className="font-semibold text-gray-900 leading-snug">
                        {group.recruiterJobTitle}
                      </h3>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                  </div>

                  {/* Stats */}
                  <div className="space-y-1.5 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Last searched {formatDate(group.lastSearchedAt)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-700">
                        {group.totalSearches} search{group.totalSearches !== 1 ? 'es' : ''}
                      </span>
                      {group.totalCandidates > 0 && (
                        <>
                          <span className="text-gray-300">·</span>
                          <span className="font-medium text-green-700">
                            {group.totalCandidates} candidates found
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
